import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Pencil, Trash2, ArrowUp, ArrowDown, Zap, Info } from "lucide-react";
import { MoneyField } from "@/components/ui/number-fields";
import { toast } from "sonner";
import { FINANCE_TYPES } from "./constants";

const EMPTY = { name: "", finance_types: [], amount_min: "", amount_max: "", lender_ids: [], enabled: true };

export default function HubRules() {
  const [rules, setRules] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [autoOn, setAutoOn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [rl, lr] = await Promise.all([api.get("/hub/rules"), api.get("/hub/lenders")]);
      setRules(rl.data.items || []);
      setAutoOn(!!rl.data.auto_routing_enabled);
      setLenders(lr.data.items || []);
    } catch { setRules([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleGlobal = async (v) => {
    try {
      await api.put("/hub/settings/auto-routing", { enabled: v });
      setAutoOn(v);
      toast.success(v ? "Auto-routing ON" : "Auto-routing OFF");
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= rules.length) return;
    const next = [...rules];
    [next[idx], next[target]] = [next[target], next[idx]];
    setRules(next);
    try {
      await api.post("/hub/rules/reorder", { ids: next.map((r) => r.id) });
    } catch (e) { toast.error("Reorder failed"); load(); }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/hub/rules/${toDelete.id}`);
      toast.success("Rule removed");
      setToDelete(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const toggleRuleEnabled = async (r) => {
    try {
      await api.put(`/hub/rules/${r.id}`, {
        name: r.name,
        finance_types: r.finance_types,
        amount_min: r.amount_min,
        amount_max: r.amount_max,
        lender_ids: r.lender_ids,
        enabled: !r.enabled,
      });
      setRules((rs) => rs.map((x) => x.id === r.id ? { ...x, enabled: !x.enabled } : x));
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const lenderById = useMemo(() => Object.fromEntries(lenders.map((l) => [l.id, l])), [lenders]);

  return (
    <div className="space-y-6" data-testid="hub-rules">
      <div className="head">
        <div>
          <div className="eyebrow">Auto-Route</div>
          <h1 className="t">Routing rules</h1>
          <p className="mt-1 text-[13.5px] text-slate-500 max-w-2xl">
            Rules fire on lead creation (admin or referrer submissions). The first enabled rule that matches finance type + amount range wins.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 rounded-[10px] border border-slate-200 bg-white px-3 h-9 text-[12px] font-medium">
            <Zap className={`size-3.5 ${autoOn ? "text-[#0055FF]" : "text-slate-400"}`} />
            <span className="text-slate-700">Global</span>
            <Switch checked={autoOn} onCheckedChange={toggleGlobal} data-testid="hub-rules-global-toggle" />
          </div>
          <Button onClick={() => setEditing({ ...EMPTY })} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="hub-rules-new">
            <Plus className="size-3.5 mr-1" /> New rule
          </Button>
        </div>
      </div>

      {!autoOn && (
        <div className="rounded-xl border border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] p-3.5 text-[13px] text-[#2F5390] flex items-start gap-2">
          <Info className="size-4 mt-0.5 shrink-0" />
          <div>
            Auto-routing is <b>OFF</b> — rules are saved but new leads won&apos;t be auto-routed until you flip the global switch.
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid place-items-center py-24 text-slate-400"><Loader2 className="size-6 animate-spin" /></div>
      ) : rules.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 border-dashed bg-white text-center py-20 px-6">
          <Zap className="size-6 text-slate-400 mx-auto" />
          <div className="mt-3 text-[15px] font-semibold text-slate-900">No routing rules yet</div>
          <p className="mt-1 text-[13.5px] text-slate-500 max-w-md mx-auto">
            Create a rule to auto-fire when new leads match a specific finance type and amount band.
          </p>
          <Button onClick={() => setEditing({ ...EMPTY })} className="mt-4 bg-[#0055FF] hover:bg-[#0044CC]">
            <Plus className="size-3.5 mr-1" /> Create your first rule
          </Button>
        </div>
      ) : (
        <div className="space-y-2.5">
          {rules.map((r, idx) => (
            <div key={r.id} className={`rounded-2xl border bg-white p-4 flex items-start gap-4 ${r.enabled ? "border-slate-200" : "border-slate-200 opacity-60"}`} data-testid={`hub-rule-row-${r.id}`}>
              <div className="flex flex-col items-center gap-1 pt-1">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-900 disabled:opacity-30" data-testid={`hub-rule-up-${r.id}`}><ArrowUp className="size-3.5" /></button>
                <span className="text-[10.5px] font-mono text-slate-400 tabular-nums">{r.priority}</span>
                <button onClick={() => move(idx, +1)} disabled={idx === rules.length - 1} className="text-slate-400 hover:text-slate-900 disabled:opacity-30" data-testid={`hub-rule-down-${r.id}`}><ArrowDown className="size-3.5" /></button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-[15px] font-semibold text-slate-900">{r.name}</div>
                  {r.enabled ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 border border-teal-200 px-2 h-5 text-[10.5px] font-medium text-teal-700">Enabled</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 px-2 h-5 text-[10.5px] font-medium text-slate-600">Paused</span>
                  )}
                </div>
                <div className="mt-1.5 text-[12.5px] text-slate-500">
                  <span className="text-slate-700 font-medium">IF</span> finance in{" "}
                  <span className="text-slate-900 font-medium">{r.finance_types.slice(0, 3).join(" / ")}{r.finance_types.length > 3 ? ` + ${r.finance_types.length - 3}` : ""}</span>
                  {" · "}amount{" "}
                  <span className="font-mono tabular-nums text-slate-900">
                    £{Math.round(r.amount_min).toLocaleString()}
                    {r.amount_max > 0 ? `–£${Math.round(r.amount_max).toLocaleString()}` : "+"}
                  </span>
                  {" — "}
                  <span className="text-slate-700 font-medium">THEN</span> route to{" "}
                  <span className="text-slate-900 font-medium">
                    {r.lender_ids.map((id) => lenderById[id]?.company_name || "—").filter(Boolean).slice(0, 3).join(" · ")}
                    {r.lender_ids.length > 3 ? ` +${r.lender_ids.length - 3}` : ""}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Switch checked={r.enabled} onCheckedChange={() => toggleRuleEnabled(r)} data-testid={`hub-rule-toggle-${r.id}`} />
                <Button variant="ghost" size="icon" onClick={() => setEditing(r)} data-testid={`hub-rule-edit-${r.id}`}><Pencil className="size-3.5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => setToDelete(r)} data-testid={`hub-rule-delete-${r.id}`}><Trash2 className="size-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <RuleDialog editing={editing} lenders={lenders} onClose={() => setEditing(null)} onSaved={load} />

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete rule?</AlertDialogTitle>
            <AlertDialogDescription>&ldquo;{toDelete?.name}&rdquo; will be removed. Leads already routed by this rule are unaffected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={remove} data-testid="hub-rule-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RuleDialog({ editing, lenders, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name || "",
        finance_types: editing.finance_types || [],
        amount_min: editing.amount_min || "",
        amount_max: editing.amount_max || "",
        lender_ids: editing.lender_ids || [],
        enabled: editing.enabled !== false,
      });
    }
  }, [editing]);

  const isNew = !editing?.id;
  const eligibleLenders = useMemo(() => lenders.filter((l) => l.status === "active"), [lenders]);

  const toggleFinance = (t) => setForm((f) => ({ ...f, finance_types: f.finance_types.includes(t) ? f.finance_types.filter((x) => x !== t) : [...f.finance_types, t] }));
  const toggleLender  = (id) => setForm((f) => ({ ...f, lender_ids: f.lender_ids.includes(id) ? f.lender_ids.filter((x) => x !== id) : [...f.lender_ids, id] }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Rule name required"); return; }
    if (form.finance_types.length === 0) { toast.error("Pick at least one finance type"); return; }
    if (form.lender_ids.length === 0) { toast.error("Pick at least one lender"); return; }
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        finance_types: form.finance_types,
        amount_min: parseFloat(form.amount_min) || 0,
        amount_max: parseFloat(form.amount_max) || 0,
        lender_ids: form.lender_ids,
        enabled: form.enabled,
      };
      if (isNew) await api.post("/hub/rules", body);
      else await api.put(`/hub/rules/${editing.id}`, body);
      toast.success(isNew ? "Rule created" : "Rule updated");
      onClose(); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="hub-rule-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "New routing rule" : "Edit rule"}</DialogTitle>
          <DialogDescription>Auto-fires on lead creation if the lead matches finance type + amount band.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Rule name *">
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Bridging £50k–£2m → Bridge-only lenders" data-testid="hub-rule-name" />
          </Field>

          <Field label="IF finance type in *">
            <div className="flex flex-wrap gap-1.5">
              {FINANCE_TYPES.map((t) => (
                <button key={t} type="button" onClick={() => toggleFinance(t)}
                        className={`inline-flex items-center rounded-[10px] px-2.5 h-7 text-[11.5px] font-medium border transition-colors ${form.finance_types.includes(t) ? "bg-[#0055FF] border-[#0055FF] text-white" : "bg-white border-slate-300 text-slate-700 hover:border-slate-400"}`}
                        data-testid={`hub-rule-finance-${t}`}>
                  {t}
                </button>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount min"><MoneyField value={form.amount_min} onChange={(v) => setForm((f) => ({ ...f, amount_min: v }))} compact testId="hub-rule-amount-min" hint="drag or type" /></Field>
            <Field label="Amount max — 0 = unlimited"><MoneyField value={form.amount_max} onChange={(v) => setForm((f) => ({ ...f, amount_max: v }))} compact testId="hub-rule-amount-max" hint="0 = unlimited" /></Field>
          </div>

          <Field label={`THEN route to lenders * (${form.lender_ids.length} selected)`}>
            {eligibleLenders.length === 0 ? (
              <div className="text-[13px] text-slate-500 italic">No active lenders. Add lenders first.</div>
            ) : (
              <div className="max-h-52 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                {eligibleLenders.map((L) => (
                  <label key={L.id} className={`flex items-center gap-3 p-2.5 cursor-pointer transition-colors ${form.lender_ids.includes(L.id) ? "bg-[#0055FF]/[0.04]" : "hover:bg-slate-50"}`} data-testid={`hub-rule-lender-${L.id}`}>
                    <input type="checkbox" checked={form.lender_ids.includes(L.id)} onChange={() => toggleLender(L.id)} className="size-4 accent-[#0055FF]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900">{L.company_name}</div>
                      <div className="text-[11px] text-slate-500">{L.company_type} · {(L.finance_types || []).slice(0, 3).join(" / ")} · £{Math.round(L.min_deal_size_gbp || 0).toLocaleString()}–£{Math.round(L.max_deal_size_gbp || 0).toLocaleString() || "∞"}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </Field>

          <div className="flex items-center justify-between pt-2">
            <label className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} data-testid="hub-rule-enabled" />
              <span>Enabled</span>
            </label>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="hub-rule-save">
                {busy ? <Loader2 className="size-4 animate-spin" /> : (isNew ? "Create rule" : "Save changes")}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-600 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
